// N38 · Phase 1.3 — 一句话 → 落库计划（纯函数：解析 + 分日拆段 + 冲突收集，UI 之外的决策都在这）
//
// planCheckin(text, { nowMin, todayKey, nextDayKey, getDay, extraAliases })
// → 失败 { ok:false, reason:'empty'|'time' }
//        { ok:false, reason:'activity', unknownWord, timeLabel }   // 时间懂了但活动词没认出 →
//          unknownWord 是抽出来的生词，交 Phase 2 追问"算哪个分类？"（抽不出来为 null）
// → 成功 {
//     ok, catId, catName, track, timeLabel, crossMidnight, matchedAlias,
//     note,   // 备注：时间+活动词之外剩下的话（"早上7点专注claude设计"→"claude设计"），
//             // 或显式「备注/备注一下」后的整段（"…吃饭备注一下和妈妈一起"→"和妈妈一起"）；没有为 null
//     parts: [{ dateK, isNextDay, updates: [{ track, idx, cat }] }],   // 直接喂 setCellsOn
//     conflicts: { count, names },   // 目标格里已有的其他分类（复述卡提示，确认即覆盖）
//   }
// matchedAlias = 命中的别名词，复述卡[改]纠正映射时把它重新教给新分类。
// 拍板（2026-06-06）：永远落真实今天（todayKey），跨午夜第二段落 nextDayKey；
// 冲突不阻止，复述卡提示后确认即覆盖（同分类重涂不算冲突）。

import { SLOT_COUNT } from '../data/slots.js';
import { parseTimeRange, makeRange } from './timeParser.js';
import { matchActivity, trackForCat } from './activityMatcher.js';
import { CAT_BY_ID } from '../data/categories.js';

// 餐别词（2026-06-06 用户提出）：① 给裸钟点纠偏（"6点吃晚饭"→18:00）；
// ② 没说钟点时跟随当前时间倒推 1 小时（拍板）；③ 备注自动加【晚餐】前缀（没其他内容也写，拍板）
const MEALS = [
  { re: /早餐|早饭/, label: '早餐', hint: '早上' },
  { re: /午餐|午饭|中饭/, label: '午餐', hint: '中午' },
  { re: /晚餐|晚饭/, label: '晚餐', hint: '晚上' },
];

function fmtMin(min) {
  const m = ((min % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

// 跨午夜时 timeParser 已把 endMin 归一成 >1440
function timeLabelOf(time) {
  return `${fmtMin(time.startMin)}–${time.endMin >= 1440 ? '次日' : ''}${fmtMin(time.endMin)}`;
}

// slot 区间 → 分日 parts：跨午夜拆「今天尾 + 明天头」（timeParser 的 endSlot 已回绕到次日格）
function buildParts(catId, track, time, todayKey, nextDayKey) {
  const mk = (dateK, isNextDay, from, to) => ({
    dateK, isNextDay,
    updates: Array.from({ length: to - from + 1 }, (_, i) => ({ track, idx: from + i, cat: catId })),
  });
  return time.crossMidnight
    ? [mk(todayKey, false, time.startSlot, SLOT_COUNT - 1), mk(nextDayKey, true, 0, time.endSlot)]
    : [mk(todayKey, false, time.startSlot, time.endSlot)];
}

// 大小写不敏感地抠掉第一次出现的子串（备注要保留原句大小写，如 "claude设计"）
function stripFirst(t, sub) {
  if (!sub) return t;
  const i = t.toLowerCase().indexOf(String(sub).toLowerCase());
  return i < 0 ? t : `${t.slice(0, i)} ${t.slice(i + sub.length)}`;
}

// 掐头去尾的口水词 + 标点收拾
function tidy(t) {
  return t
    .replace(/[，。！？、；：,.!?;:\s]+/g, ' ').trim()
    .replace(/^(我|在|是|去|又|就|然后|接着|一直|都在|开始|刚才|刚刚)+/, '')
    .replace(/(了|的|呢|啊|吧|哦|呀|来着)+$/, '')
    .trim();
}

// 从原句里抠出活动生词：去掉时间表达式 + 掐口水词。抠不出 → null（没法教）。
export function extractActivityWord(text, timeMatched) {
  return tidy(stripFirst(String(text || ''), timeMatched)) || null;
}

// 时间 + 活动词之外剩下的话 = 备注（"早上7点专注claude设计" → "claude设计"）。没剩 → null。
export function extractNote(text, timeMatched, alias) {
  return tidy(stripFirst(stripFirst(String(text || ''), timeMatched), alias)) || null;
}

// 显式备注：句子里说「备注」/「备注一下」，后面整段原样进备注（只轻收拾头尾，
// 不掐"在/了"——那是用户原话）。解析只看「备注」前的 head，防止备注里的数字/活动词
// 干扰时间和分类识别（"晚上8点看剧备注10点睡"的 10点 不会被误读）。
// 注意只掐"一下"不掐单独的"下"："备注下周计划"的"下周"是内容。
export function splitExplicitNote(text) {
  const t = String(text || '');
  const i = t.indexOf('备注');
  if (i < 0) return { head: t, tail: null };
  const tail = t.slice(i + 2)
    .replace(/^\s*(一下)?[\s是的：:，,、]*/, '')
    .replace(/[\s，。、,.！!？?；;]+$/, '')
    .trim();
  return { head: t.slice(0, i), tail: tail || null };
}

export function planCheckin(text, { nowMin = null, todayKey, nextDayKey, getDay = () => null, extraAliases = {} } = {}) {
  if (!text || !String(text).trim()) return { ok: false, reason: 'empty' };
  // 「备注/备注一下」后面的整段先摘出来，时间和活动只在 head 里找
  const { head, tail: explicitNote } = splitExplicitNote(text);
  const meal = MEALS.find(m => m.re.test(head)) || null;
  let time = parseTimeRange(head, { nowMin, periodHint: meal ? meal.hint : null });
  // 餐别词但没说钟点 → 跟随当前时间倒推 1 小时（拍板 2026-06-06）
  if (!time.ok && meal && nowMin != null) time = makeRange(Math.max(0, nowMin - 60), nowMin, 'relative', '');
  if (!time.ok) return { ok: false, reason: 'time' };
  const timeLabel = timeLabelOf(time);
  const act = matchActivity(head, { extraAliases });
  if (!act) return { ok: false, reason: 'activity', unknownWord: extractActivityWord(head, time.matched), timeLabel };

  const parts = buildParts(act.catId, act.track, time, todayKey, nextDayKey);
  const conflicts = collectConflicts(parts, act.catId, getDay);

  return {
    ok: true,
    catId: act.catId,
    catName: act.name,
    track: act.track,
    timeLabel,
    crossMidnight: !!time.crossMidnight,
    matchedAlias: act.matchedAlias,
    // 备注 = head 里时间+活动词之外的剩余文字 + 显式「备注」后的整段（两者都有就拼起来）；
    // 有餐别词时加【晚餐】前缀（没其他内容也写）。写到段内每一格（与快速应用同口径）
    note: (() => {
      let auto = extractNote(head, time.matched, act.matchedAlias);
      // 剩下的若只是同类活动词残渣（"吃了晚饭"匹配走"晚饭"后剩"吃"）→ 不算备注
      if (auto) {
        const m2 = matchActivity(auto, { extraAliases });
        if (m2 && m2.catId === act.catId && m2.matchedAlias === auto.toLowerCase()) auto = null;
      }
      const body = [auto, explicitNote].filter(Boolean).join(' ');
      if (meal) return `【${meal.label}】${body}`;
      return body || null;
    })(),
    parts,
    conflicts,
  };
}

// 冲突收集：parts 目标格上已有的其他分类（同分类重涂不算）
function collectConflicts(parts, catId, getDay) {
  const names = [];
  let count = 0;
  parts.forEach(p => {
    const day = getDay(p.dateK);
    p.updates.forEach(u => {
      const tr = day && day.tracks && day.tracks[u.track];
      if (!tr) return;
      const cur = tr[u.idx];
      if (cur != null && cur !== catId) {
        count++;
        const c = CAT_BY_ID[cur];
        const nm = (c && (c.name || c.label)) || cur;
        if (!names.includes(nm)) names.push(nm);
      }
    });
  });
  return { count, names };
}

// 直接指定「分类 + 时段」造 plan（Phase 4 推荐流一键打卡用，Phase 5 番茄入轨可复用），
// 结构与 planCheckin 完全一致，直接喂确认流/applyCheckin。time = timeParser 的区间结果。
export function planDirect(catId, time, { todayKey, nextDayKey, getDay = () => null } = {}) {
  const track = trackForCat(catId);
  const c = CAT_BY_ID[catId];
  const parts = buildParts(catId, track, time, todayKey, nextDayKey);
  return {
    ok: true,
    catId,
    catName: (c && (c.name || c.label)) || catId,
    track,
    timeLabel: timeLabelOf(time),
    crossMidnight: !!time.crossMidnight,
    matchedAlias: null,
    note: null,
    parts,
    conflicts: collectConflicts(parts, catId, getDay),
  };
}

// 改分类（复述卡[改分类]）：在现有 plan 上直接打补丁——换分类/换轨道/重算冲突，
// 时段和备注（含手改过的）一概不动。绝不重新解析原句：重解析会丢掉手改的备注、
// 还可能匹配回别的词，造成"嘴上说怦然、落库成预备"（B14，用户 2026-06-06 实测）。
export function retargetPlan(plan, catId, { getDay = () => null } = {}) {
  const track = trackForCat(catId);
  const c = CAT_BY_ID[catId];
  const parts = plan.parts.map(p => ({
    ...p,
    updates: p.updates.map(u => ({ ...u, track, cat: catId })),
  }));
  return {
    ...plan,
    catId,
    catName: (c && (c.name || c.label)) || catId,
    track,
    parts,
    conflicts: collectConflicts(parts, catId, getDay),
  };
}
